// Card 09, depth tints (R2616, R2713, R2726; ui-map-interface.md §17.3.09,
// v2-the-deep.md §13; mockup round 088 card 04 and round 116 cards 01,
// 02, 05 and 09).
//
// A chart paints its water in blues by depth: the deeper the band, the
// darker the sea. The shelf and the slope take light, and the deep and
// the abyss take the dark and a cold hue (round 143 card 08, R3229). The
// picture is per-pixel, so it is ONE full-world quad of
// the depth layer (`fast/layers/depth.ts`), and the tints are no batch.
// It hands the band field and the reading to the layer through the scene,
// as card 01 hands its lines to the terrain program. The depth look (the
// feathered tints, the tint steps alone, or the flat tints of the look
// `chart`) is the World field `depth`,
// which the key `\` flips for the session (`depthlook.ts`, R2726). The
// shoal stipple and the edge seam are the pieces of cards 71 and 72
// (`depth-marks.ts`).
// The feathered and the stepped look draw no rule on a band edge (round
// 116 brief).
//
// **THE LOOK `chart` DRAWS THE ISOBATHS** (round 145 card 02, R3278).
// While the tints draw in the look `chart` (`ChartScene.tintLook`), the
// piece traces the band edges of the field (`depth-edges.ts`) and draws a
// thin isobath on each in the `fill` ink, broken for its figure: the top
// of the deeper band in metres, in the chart text of the reading. The
// trace runs on a hello, a survey and a flip of the look, and a reading or
// a font keeps it. No other look pays for it.
//
// **IT DRAWS WHAT THE WIRE SENDS.** The band field is `Hello.depth`
// (R2609), nil while the flag `depth` is off, so a world with the flag off
// draws nothing. A cell with no band (rock, or water the survey does not
// hold) takes no tint. The piece reads no flag (§14.1 rule 5).
//
// **THE GATE IS THE SCENE'S.** The controller never calls a piece the
// gate has dropped, so `MapScene.setVisibility` hands the gate of this
// piece to the layer on every frame, and the layer keeps the field while
// the piece is hidden.

import { CHART } from '../../../gen/presentation';
import { COLS, ROWS } from '../../../world';
import { fieldRev } from '../../../deep';
import { runWidth } from '../../../render/text-run';
import type { ChartContext, ChartSinks, Piece } from '../context';
import {
  EDGE_LEVELS, cutGaps, edgeFigure, placeFigures, traceBandEdges, type EdgeFigure, type EdgeTrace,
} from './depth-edges';
import { CHART_INK, faceOf, inkOf } from './shared';

const T = CHART.pieces.depth_tints;

export class DepthTintsPiece implements Piece {
  readonly id = 'depth_tints' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The sea the fight happened in. It stays under a replay film. */
  readonly film = 'keep' as const;
  /** A survey grows the band field in place, so it hands it over again.
   *  A flip of the look starts or ends the isobaths of the look `chart`,
   *  and a font moves the width of their gaps (R3278). */
  readonly rebuildOn = ['hello', 'reading', 'survey', 'look', 'font'] as const;

  /** The trace of the band edges, and the field and revision it read. */
  private trace: EdgeTrace | null = null;
  private tracedFor: Uint8Array | null = null;
  private tracedKey = '';
  /** How many times the field was traced. The tests read it. */
  traces = 0;
  /** The isobaths of the last pack with the gaps cut, their figures, and
   *  the gap of each edge in world px. Null in every other look. */
  lines: EdgeTrace['segs'] | null = null;
  figures: EdgeFigure[] = [];
  private gaps: number[] = [];

  visible(ctx: ChartContext): boolean {
    return ctx.state.depthBands !== null;
  }

  /** Writes the band field and the reading into the depth layer, and
   *  traces the band edges while the look `chart` draws the tints. */
  rebuild(ctx: ChartContext): void {
    const bands = ctx.state.depthBands;
    ctx.scene.setDepthField?.(ctx.reading, bands);
    this.lines = null;
    this.figures = [];
    if (!bands || ctx.scene.tintLook !== 'chart') return;
    // A survey pass writes the field in place, so the key holds the
    // revision of the fields and the grid too.
    const key = `${COLS}x${ROWS}:${fieldRev()}`;
    if (!this.trace || this.tracedFor !== bands || this.tracedKey !== key) {
      this.trace = traceBandEdges(bands);
      this.tracedFor = bands;
      this.tracedKey = key;
      this.traces++;
    }
    this.figures = placeFigures(bands, this.trace);
    // The gap of each edge is its figure's width in the face of the
    // reading, and a pad on each side.
    const face = faceOf(ctx.reading);
    this.gaps = EDGE_LEVELS.map((_, k) => runWidth(edgeFigure(k), ctx.glyphs.stepMetrics(face, T.text.main))
      + 2 * T.fig.chart_gap_pad_px);
    this.lines = cutGaps(this.trace, this.figures, this.gaps);
  }

  /** The isobaths of the look `chart`, and nothing in the other looks:
   *  their picture is the depth layer's quad alone. */
  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const lines = this.lines;
    if (!lines) return;
    const r = T[ctx.reading];
    const ink = inkOf(r.fill, CHART_INK.pale);
    const on = r.dash[0] ?? 0, off = r.dash[1] ?? 0;
    for (let k = 0; k < lines.length; k++) {
      const a = r.arr.chart_edge_alpha[k] ?? 0;
      if (a <= 0) continue;
      const s = lines[k];
      for (let i = 0; i + 3 < s.length; i += 4) {
        sinks.segments.line(s[i], s[i + 1], s[i + 2], s[i + 3], r.width, ink, a, 0, on, off);
      }
    }
    const face = faceOf(ctx.reading);
    const fa = r.fig.chart_figure_alpha;
    for (const f of this.figures) {
      // The figure rides a straight path along the edge, centred on its
      // gap and turned to read left to right. The path is a gap long on
      // each side, longer than half the figure.
      const half = this.gaps[f.k] ?? 0;
      const path = [
        { x: f.x - f.tx * half, y: f.y - f.ty * half },
        { x: f.x + f.tx * half, y: f.y + f.ty * half },
      ];
      ctx.glyphs.wordAlong(sinks.quads, edgeFigure(f.k), path, face, T.text.main, ink, fa,
        { anchor: 'centre', startS: half, upright: false });
    }
  }
}

/** The one instance the registry holds, and the scene asks about. */
export const depthTints = new DepthTintsPiece();
